import {
  hasRole,
  type Role,
  type SandboxCapabilities,
  type SandboxDto,
  type SandboxExecutionDto,
  type SandboxRuntime,
  type ToolDto,
} from '@agent-platform/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { Play, ShieldAlert, ShieldCheck, Terminal } from 'lucide-react';
import { type FormEvent, type KeyboardEvent, useState } from 'react';
import { toast } from 'sonner';
import { ConfirmDeleteDialog } from '@/components/confirm-delete-dialog';
import { EmptyState } from '@/components/empty-state';
import { ListSearch, NoMatches, useListSearch } from '@/components/list-search';
import { ErrorAlert } from '@/components/error-alert';
import { PageHeader } from '@/components/page-header';
import { RequireTenant } from '@/components/require-tenant';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { api, errorMessage } from '@/lib/api';
import { formatDuration, formatRelative } from '@/lib/format';
import { cn } from '@/lib/utils';

export const RUNTIME_LABELS: Record<SandboxRuntime, string> = {
  python: 'Python',
  node: 'Node.js',
  bash: 'Bash',
};

const SAMPLE_CODE: Record<SandboxRuntime, string> = {
  python: 'import json, math\nprint(json.dumps({"sqrt2": math.sqrt(2)}))\n',
  node: 'const data = { now: new Date().toISOString(), sum: [1, 2, 3].reduce((a, b) => a + b) };\nconsole.log(JSON.stringify(data));\n',
  bash: 'echo "Hello from $(uname -s)"\n',
};

function useCapabilities(tenantId: string) {
  return useQuery({
    queryKey: ['sandbox-capabilities', tenantId],
    queryFn: () => api<SandboxCapabilities>('/sandboxes/capabilities'),
    staleTime: 60_000,
  });
}

/** Read-only view of what the server enforces on local sandboxes (`SANDBOX_ISOLATION`). */
function IsolationPanel({ tenantId, sandbox }: { tenantId: string; sandbox?: SandboxDto }) {
  const capabilities = useCapabilities(tenantId);
  if (!capabilities.data) return null;
  const isolated = capabilities.data.isolation === 'bwrap';
  const Icon = isolated ? ShieldCheck : ShieldAlert;
  const limits = sandbox
    ? `${formatDuration(sandbox.timeoutMs)} wall clock, ${Math.ceil(sandbox.timeoutMs / 1000) + 1} s CPU, ${Math.round(sandbox.outputMaxBytes / 1024)} KB output, 200 MB files`
    : 'Wall-clock and CPU time, output size and file size are capped on every run.';
  const rows: [string, string][] = isolated
    ? [
        ['Network', 'None — no internet, database or cloud metadata'],
        ['Server access', 'Cannot see the API process, its files, secrets or other runs'],
        ['Filesystem', 'System paths read-only; only its own scratch folder is writable'],
        [
          'Processes',
          'Own process namespace; no nested namespaces; all processes killed when the run ends',
        ],
        ['Environment', 'Clean — only PATH, HOME and LANG'],
        ['Limits', limits],
      ]
    : [
        ['Network', 'Unrestricted'],
        ['Server access', 'Runs as the API user and can read its files and secrets'],
        ['Environment', 'Clean — only PATH, HOME and LANG'],
        ['Limits', limits],
      ];
  return (
    <Card className={cn('self-start', !isolated && 'border-destructive/50')}>
      <CardHeader className="flex flex-row items-start gap-3 space-y-0">
        <Icon
          className={cn(
            'mt-0.5 size-5 shrink-0',
            isolated ? 'text-emerald-500' : 'text-destructive',
          )}
          aria-hidden
        />
        <div className="grid gap-1">
          <CardTitle className="flex items-center gap-2 text-base">
            Local sandbox isolation
            <Badge variant={isolated ? 'secondary' : 'destructive'}>
              {isolated ? 'Bubblewrap' : 'Not isolated'}
            </Badge>
          </CardTitle>
          <CardDescription>
            {isolated
              ? 'Set by the server for every tenant; it cannot be turned off from the console.'
              : 'This server runs local sandboxes without isolation (SANDBOX_ISOLATION=none). Use them only for trusted code.'}
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent>
        <dl className="grid gap-2 text-sm sm:grid-cols-[9rem_1fr]">
          {rows.map(([term, detail]) => (
            <div key={term} className="contents">
              <dt className="text-muted-foreground">{term}</dt>
              <dd>{detail}</dd>
            </div>
          ))}
        </dl>
      </CardContent>
    </Card>
  );
}

function NewSandboxDialog({ tenantId }: { tenantId: string }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const capabilities = useCapabilities(tenantId);
  const allowlist = capabilities.data?.localAllowlist ?? [];
  const isolated = capabilities.data?.isolation === 'bwrap';
  const [open, setOpen] = useState(false);
  const [provider, setProvider] = useState<'local' | 'remote'>('remote');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [runtime, setRuntime] = useState<SandboxRuntime>('python');
  const [command, setCommand] = useState('');
  const [url, setUrl] = useState('');
  const [headers, setHeaders] = useState(
    '{\n  "Authorization": { "template": "Bearer {{env.SANDBOX_TOKEN}}" }\n}',
  );
  const [timeoutMs, setTimeoutMs] = useState(30_000);
  const [jsonError, setJsonError] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: (body: unknown) => api<SandboxDto>('/sandboxes', { method: 'POST', body }),
    onSuccess: (s) => {
      toast.success(`Sandbox ${s.name} created`);
      void queryClient.invalidateQueries({ queryKey: ['sandboxes', tenantId] });
      setOpen(false);
      void navigate({ to: '/sandboxes/$sandboxId', params: { sandboxId: s.id } });
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const common = { name, description, runtime, timeoutMs };
    if (provider === 'local') return create.mutate({ ...common, provider, command });
    try {
      const parsed: unknown = headers.trim() ? JSON.parse(headers) : {};
      setJsonError(null);
      create.mutate({ ...common, provider, url, headers: parsed });
    } catch {
      setJsonError('Headers must be valid JSON.');
    }
  };
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) create.reset();
      }}
    >
      <DialogTrigger asChild>
        <Button>New sandbox</Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Create a sandbox</DialogTitle>
          <DialogDescription>
            A place to run code: for agents (as a tool), for workflow steps, and from this console.
          </DialogDescription>
        </DialogHeader>
        <form className="grid gap-4" noValidate onSubmit={submit}>
          <ErrorAlert error={create.error} />
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="sb-name">Name</Label>
              <Input
                id="sb-name"
                className="font-mono"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="python-runner"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="sb-runtime">Language</Label>
              <Select value={runtime} onValueChange={(v) => setRuntime(v as SandboxRuntime)}>
                <SelectTrigger id="sb-runtime" aria-label="Language">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(RUNTIME_LABELS) as SandboxRuntime[]).map((r) => (
                    <SelectItem key={r} value={r}>
                      {RUNTIME_LABELS[r]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="sb-description">Description</Label>
            <Input
              id="sb-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="sb-provider">Where code runs</Label>
            <Select value={provider} onValueChange={(v) => setProvider(v as 'local' | 'remote')}>
              <SelectTrigger id="sb-provider" aria-label="Where code runs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="remote">Remote runner (HTTP)</SelectItem>
                <SelectItem value="local" disabled={allowlist.length === 0}>
                  Local interpreter{allowlist.length === 0 ? ' (not enabled on this server)' : ''}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          {provider === 'local' ? (
            <div className="grid gap-1.5">
              <Label htmlFor="sb-command">Interpreter</Label>
              <Select value={command} onValueChange={setCommand}>
                <SelectTrigger id="sb-command" aria-label="Interpreter">
                  <SelectValue placeholder="Choose an allowed interpreter" />
                </SelectTrigger>
                <SelectContent>
                  {allowlist.map((c) => (
                    <SelectItem key={c} value={c}>
                      <span className="font-mono">{c}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-muted-foreground text-xs">
                {isolated
                  ? 'Runs on the API host in an isolated Bubblewrap sandbox: no network, no access to the server or other runs, a clean environment, a time limit and output caps.'
                  : 'Runs on the API host with a clean environment, a time limit and output caps. Not isolated on this server — use it only for trusted code.'}
              </p>
            </div>
          ) : (
            <>
              <div className="grid gap-1.5">
                <Label htmlFor="sb-url">Runner URL</Label>
                <Input
                  id="sb-url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://runner.example.com/execute"
                />
                <p className="text-muted-foreground text-xs">
                  Receives <code>{'{ runtime, code, timeoutMs }'}</code> and returns{' '}
                  <code>{'{ stdout, stderr, exitCode }'}</code>.
                </p>
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="sb-headers">Headers (JSON)</Label>
                <Textarea
                  id="sb-headers"
                  className="font-mono text-xs"
                  value={headers}
                  onChange={(e) => setHeaders(e.target.value)}
                />
                {jsonError && <p className="text-destructive text-xs">{jsonError}</p>}
              </div>
            </>
          )}
          <div className="grid gap-1.5">
            <Label htmlFor="sb-timeout">Time limit (ms)</Label>
            <Input
              id="sb-timeout"
              type="number"
              min={100}
              max={120000}
              value={timeoutMs}
              onChange={(e) => setTimeoutMs(Number(e.target.value))}
            />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={create.isPending}>
              Create sandbox
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SandboxList({ tenantId, role }: { tenantId: string; role: Role }) {
  const sandboxes = useQuery({
    queryKey: ['sandboxes', tenantId],
    queryFn: () => api<{ items: SandboxDto[] }>('/sandboxes'),
  });
  const rows = sandboxes.data?.items ?? [];
  const search = useListSearch(rows, (s) => [
    s.name,
    s.description,
    s.runtime,
    RUNTIME_LABELS[s.runtime],
    s.provider,
  ]);
  return (
    <>
      <PageHeader
        title="Sandboxes"
        description="Code-execution environments for agents, workflows and quick experiments."
        actions={hasRole(role, 'admin') && <NewSandboxDialog tenantId={tenantId} />}
      />
      <ErrorAlert error={sandboxes.error} />
      <div className="mb-4">
        <IsolationPanel tenantId={tenantId} />
      </div>
      {sandboxes.isPending ? (
        <Skeleton className="h-32" />
      ) : rows.length === 0 ? (
        <EmptyState title="No sandboxes yet">
          Create one, then turn it into a tool so agents can run code — calculations, data
          wrangling, quick scripts.
        </EmptyState>
      ) : (
        <>
          <ListSearch
            className="mb-4"
            value={search.query}
            onChange={search.setQuery}
            label="Search sandboxes"
            placeholder="Search by name, runtime or provider"
          />
          <ul className="grid gap-3 md:grid-cols-2">
            {search.filtered.map((s) => (
              <li key={s.id}>
                <Link
                  to="/sandboxes/$sandboxId"
                  params={{ sandboxId: s.id }}
                  className="block h-full"
                >
                  <Card className="hover:bg-muted/40 h-full transition-colors">
                    <CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0">
                      <div className="grid gap-1">
                        <CardTitle className="flex items-center gap-2 font-mono text-base">
                          <Terminal className="size-4" aria-hidden /> {s.name}
                        </CardTitle>
                        <CardDescription>{s.description || 'No description'}</CardDescription>
                      </div>
                      <Badge variant="secondary">{RUNTIME_LABELS[s.runtime]}</Badge>
                    </CardHeader>
                    <CardContent className="text-muted-foreground text-xs">
                      {s.provider === 'local' ? (
                        <>
                          Local · <code>{s.command}</code>
                        </>
                      ) : (
                        <>
                          Remote · <code className="break-all">{s.url}</code>
                        </>
                      )}{' '}
                      · limit {formatDuration(s.timeoutMs)}
                    </CardContent>
                  </Card>
                </Link>
              </li>
            ))}
          </ul>
          {search.filtered.length === 0 && <NoMatches query={search.query} />}
        </>
      )}
    </>
  );
}

export function SandboxesPage() {
  return (
    <RequireTenant>
      {(tenant) => <SandboxList tenantId={tenant.id} role={tenant.role} />}
    </RequireTenant>
  );
}

// --- Detail ---

export const EXECUTION_VARIANT = {
  succeeded: 'secondary',
  failed: 'destructive',
  timeout: 'destructive',
  error: 'destructive',
} as const;

function ExecutionOutput({ execution }: { execution: SandboxExecutionDto }) {
  return (
    <div className="grid gap-2" data-testid="sandbox-output">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <Badge variant={EXECUTION_VARIANT[execution.status]}>{execution.status}</Badge>
        <span className="text-muted-foreground">
          exit {execution.exitCode ?? '—'} · {formatDuration(execution.durationMs)}
        </span>
        {execution.message && <span className="text-destructive">{execution.message}</span>}
      </div>
      <pre className="max-h-80 overflow-auto rounded-md bg-zinc-950 p-3 font-mono text-xs whitespace-pre-wrap text-zinc-100">
        {execution.stdout || <span className="text-zinc-500">(no output)</span>}
        {execution.stderr && <span className="text-red-300">{`\n${execution.stderr}`}</span>}
      </pre>
    </div>
  );
}

function CreateToolDialog({ tenantId, sandbox }: { tenantId: string; sandbox: SandboxDto }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(`run_${sandbox.runtime}`);
  const [description, setDescription] = useState(
    `Runs a ${RUNTIME_LABELS[sandbox.runtime]} program and returns its stdout, stderr and exit code. Use it for calculations and data processing.`,
  );
  const create = useMutation({
    mutationFn: () =>
      api<ToolDto>(`/sandboxes/${sandbox.id}/tool`, {
        method: 'POST',
        body: { name, description },
      }),
    onSuccess: (t) => {
      toast.success(`Tool ${t.name} created`, {
        description: 'Attach it to an agent on its Tools & MCP tab.',
      });
      void queryClient.invalidateQueries({ queryKey: ['tools', tenantId] });
      setOpen(false);
    },
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline">Create agent tool</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Give agents this sandbox</DialogTitle>
          <DialogDescription>
            Creates a tool that takes <code>code</code> and runs it here. Every call is recorded in
            this sandbox’s history and the run’s trace.
          </DialogDescription>
        </DialogHeader>
        <ErrorAlert error={create.error} />
        <div className="grid gap-1.5">
          <Label htmlFor="sb-tool-name">Tool name</Label>
          <Input
            id="sb-tool-name"
            className="font-mono"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="sb-tool-description">Description (the model reads this)</Label>
          <Textarea
            id="sb-tool-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        <DialogFooter>
          <Button disabled={create.isPending} onClick={() => create.mutate()}>
            Create tool
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SandboxDetail({
  tenantId,
  role,
  sandboxId,
}: {
  tenantId: string;
  role: Role;
  sandboxId: string;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const canEdit = hasRole(role, 'admin');
  const sandbox = useQuery({
    queryKey: ['sandbox', tenantId, sandboxId],
    queryFn: () => api<SandboxDto>(`/sandboxes/${sandboxId}`),
  });
  const executions = useQuery({
    queryKey: ['sandbox-executions', tenantId, sandboxId],
    queryFn: () => api<{ items: SandboxExecutionDto[] }>(`/sandboxes/${sandboxId}/executions`),
  });
  const [code, setCode] = useState<string | null>(null);
  const [last, setLast] = useState<SandboxExecutionDto | null>(null);
  const run = useMutation({
    mutationFn: (source: string) =>
      api<SandboxExecutionDto>(`/sandboxes/${sandboxId}/execute`, {
        method: 'POST',
        body: { code: source },
      }),
    onSuccess: (e) => {
      setLast(e);
      void queryClient.invalidateQueries({ queryKey: ['sandbox-executions', tenantId, sandboxId] });
    },
  });
  const remove = useMutation({
    mutationFn: () => api<void>(`/sandboxes/${sandboxId}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['sandboxes', tenantId] });
      void navigate({ to: '/sandboxes' });
    },
  });
  const [deleting, setDeleting] = useState(false);

  if (sandbox.error) return <ErrorAlert error={sandbox.error} />;
  if (!sandbox.data) return <Skeleton className="h-64" />;
  const s = sandbox.data;
  const source = code ?? SAMPLE_CODE[s.runtime];
  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && canEdit && !run.isPending) {
      e.preventDefault();
      run.mutate(source);
    }
  };

  return (
    <>
      <PageHeader
        title={s.name}
        description={`${RUNTIME_LABELS[s.runtime]} · ${s.provider === 'local' ? `local ${s.command}` : `remote ${s.url}`} · limit ${formatDuration(s.timeoutMs)}`}
        actions={
          <>
            <Button variant="outline" asChild>
              <Link to="/sandboxes">All sandboxes</Link>
            </Button>
            {canEdit && <CreateToolDialog tenantId={tenantId} sandbox={s} />}
            {canEdit && (
              <>
                <Button variant="destructive" onClick={() => setDeleting(true)}>
                  Delete
                </Button>
                <ConfirmDeleteDialog
                  open={deleting}
                  onOpenChange={setDeleting}
                  title={`Delete ${s.name}?`}
                  description="The sandbox and its execution history are deleted. This cannot be undone."
                  onConfirm={() => remove.mutateAsync()}
                />
              </>
            )}
          </>
        }
      />
      <div className="grid gap-4 lg:grid-cols-[1fr_22rem]">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
            <CardTitle className="text-base">Run code</CardTitle>
            {canEdit && (
              <Button
                size="sm"
                disabled={run.isPending || !source.trim()}
                onClick={() => run.mutate(source)}
              >
                <Play aria-hidden /> {run.isPending ? 'Running…' : 'Run'}
              </Button>
            )}
          </CardHeader>
          <CardContent className="grid gap-3">
            <Textarea
              aria-label="Code"
              spellCheck={false}
              className="min-h-72 font-mono text-xs"
              value={source}
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={onKeyDown}
              disabled={!canEdit}
            />
            <p className="text-muted-foreground text-xs">⌘/Ctrl + Enter runs the code.</p>
            {run.error && (
              <Alert variant="destructive">
                <AlertDescription>{errorMessage(run.error)}</AlertDescription>
              </Alert>
            )}
            {last && <ExecutionOutput execution={last} />}
          </CardContent>
        </Card>
        <div className="grid gap-4 self-start">
          {s.provider === 'local' && <IsolationPanel tenantId={tenantId} sandbox={s} />}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">History</CardTitle>
              <CardDescription>Console, agent and workflow executions.</CardDescription>
            </CardHeader>
            <CardContent>
              <ErrorAlert error={executions.error} />
              {(executions.data?.items.length ?? 0) === 0 ? (
                <p className="text-muted-foreground text-sm">Nothing has run yet.</p>
              ) : (
                <ol className="grid gap-1.5" aria-label="Executions">
                  {executions.data?.items.map((e) => (
                    <li key={e.id}>
                      <button
                        type="button"
                        className={cn(
                          'hover:bg-muted grid w-full gap-0.5 rounded-md border px-2 py-1.5 text-left text-xs',
                          last?.id === e.id && 'bg-muted',
                        )}
                        onClick={() => {
                          setLast(e);
                          setCode(e.code);
                        }}
                      >
                        <span className="flex items-center gap-2">
                          <Badge variant={EXECUTION_VARIANT[e.status]}>{e.status}</Badge>
                          <span className="capitalize">{e.source}</span>
                          <span className="text-muted-foreground ml-auto">
                            {formatRelative(e.createdAt)}
                          </span>
                        </span>
                        <span className="text-muted-foreground truncate font-mono">
                          {e.code.split('\n')[0]}
                        </span>
                      </button>
                      {e.runId && (
                        <Link
                          to="/traces/runs/$runId"
                          params={{ runId: e.runId }}
                          className="text-muted-foreground px-2 text-xs underline"
                        >
                          agent trace
                        </Link>
                      )}
                    </li>
                  ))}
                </ol>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}

export function SandboxDetailPage() {
  const { sandboxId } = useParams({ from: '/app/sandboxes/$sandboxId' });
  return (
    <RequireTenant>
      {(tenant) => <SandboxDetail tenantId={tenant.id} role={tenant.role} sandboxId={sandboxId} />}
    </RequireTenant>
  );
}
